import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import MyAppRequests from './MyAppRequests';
import HowItWorks from './HowItWorks';
import { toastError, toastSuccess } from '../../lib/toast';
import AppRequestForm from './AppRequestForm';
import ExploreApps from './ExploreApps';
import './buildApp.css';

const VALID_TABS = new Set(['mine', 'build', 'explore']);

function readTabFromHash() {
  if (typeof window === 'undefined') return 'mine';
  const m = window.location.hash.match(/sub=([a-z]+)/i);
  return m && VALID_TABS.has(m[1]) ? m[1] : 'mine';
}

// The user detail page deep-links here with `#edit=<id>` to open that pending
// request directly in the edit form. Returns the id (number) or null.
function readEditIdFromHash() {
  if (typeof window === 'undefined') return null;
  const m = window.location.hash.match(/edit=(\d+)/);
  return m ? Number(m[1]) : null;
}

function writeTabToHash(sub) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  params.set('sub', sub);
  const hash = `#${params.toString()}`;
  if (window.location.hash !== hash) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
  }
}

export default function BuildAppPage() {
  const [catalog, setCatalog] = useState([]);
  const [apps, setApps] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(readTabFromHash);
  const [editApp, setEditApp] = useState(null); // /detail payload being edited, or null

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => { writeTabToHash(active); }, [active]);

  const loadMine = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/app_requests/mine`, { withCredentials: true });
      if (mountedRef.current) setApps(res.data);
    } catch {
      // Non-fatal: the list will refresh on next load.
    }
  }, []);

  useEffect(() => {
    let activeLoad = true;
    (async () => {
      try {
        const [cat, mine] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/tools/catalog-for-apps`, { withCredentials: true }),
          axios.get(`${API_BASE_URL}/api/app_requests/mine`, { withCredentials: true }),
        ]);
        if (!activeLoad) return;
        setCatalog(cat.data);
        setApps(mine.data);
      } catch {
        if (activeLoad) setError('Failed to load. Try again.');
      } finally {
        if (activeLoad) setLoading(false);
      }
    })();
    return () => { activeLoad = false; };
  }, []);

  const cancel = useCallback(async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/app_requests/${id}`, { withCredentials: true });
      await loadMine();
      toastSuccess('Request cancelled.');
    } catch {
      toastError('Couldn’t cancel that request. Try again.');
    }
  }, [loadMine]);

  const handleSubmitted = useCallback(async () => {
    await loadMine();
    if (mountedRef.current) { setEditApp(null); setActive('mine'); }
  }, [loadMine]);

  // Fetch full detail for a pending app, then switch to the edit view.
  const startEdit = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/app_requests/${id}/detail`, { withCredentials: true });
      if (!mountedRef.current) return;
      setEditApp(res.data);
      setActive('edit');
    } catch {
      toastError('Couldn’t load that request to edit.');
    }
  }, []);

  const cancelEdit = useCallback(() => { setEditApp(null); setActive('mine'); }, []);

  // Deep link from the user detail page: /build-app#edit=<id> opens that pending
  // request in the edit form once. Strip the hash so refresh/back doesn't re-fire.
  useEffect(() => {
    const editId = readEditIdFromHash();
    if (!editId) return;
    try { window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`); } catch { /* ignore */ }
    startEdit(editId);
  }, [startEdit]);

  if (loading) return <div className="container"><p>Loading…</p></div>;

  const isForm = active === 'build' || active === 'edit';
  const title = active === 'edit' ? 'Edit app request' : active === 'build' ? 'Build an app' : active === 'explore' ? 'Explore apps' : 'My app requests';

  // The list view aligns with the "My tools" page (shared .container width +
  // gutters). The build/edit form stays narrower for readability (.ba-page--form).
  return (
    <div className="container page-fill">
      <div className={`ba-page page-fill${isForm ? ' ba-page--form' : ' ba-page--list'}`}>
      {isForm ? (
        <button
          type="button"
          className="btn btn-secondary btn-small ba-back"
          onClick={active === 'edit' ? cancelEdit : () => setActive('mine')}
        >
          ← Back to my apps
        </button>
      ) : null}

      <div className="ba-header page-head">
        <div className="ba-header-text page-title-row">
          <h1 className="page-title">{title}</h1>
          <span className="page-subtitle">
            {active === 'edit'
              ? 'Update your pending request before an admin reviews it.'
              : active === 'explore'
              ? 'Browse apps your colleagues are building and ask to join the team.'
              : 'Request a new internal app and the data access it needs from your tools.'}
          </span>
        </div>
        {active === 'mine' && (
          <div className="ba-header-cta-group">
            <button type="button" className="btn btn-secondary ba-header-cta" onClick={() => setActive('explore')}>
              Explore apps
            </button>
            <button type="button" className="btn btn-primary ba-header-cta" onClick={() => setActive('build')}>
              + Build an app
            </button>
          </div>
        )}
      </div>

      {error && <p className="ba-error">{error}</p>}

      <div className="ba-page-body">
      {active === 'mine' && (
        <>
          {/* First-timers + collaborators get the tutorial open; once they
              have apps, it collapses so the list leads. */}
          <HowItWorks defaultOpen={apps.length === 0} />
          <MyAppRequests apps={apps} onCancel={cancel} onEdit={startEdit} onBuild={() => setActive('build')} />
        </>
      )}
      {active === 'explore' && (
        <>
          <div className="ba-explore-intro">
            <button type="button" className="btn btn-secondary btn-small ba-back" onClick={() => setActive('mine')}>
              ← Back to my apps
            </button>
          </div>
          <ExploreApps />
        </>
      )}
      {active === 'build' && (
        <AppRequestForm catalog={catalog} onSubmitted={handleSubmitted} />
      )}
      {active === 'edit' && editApp && (
        <AppRequestForm
          catalog={catalog}
          onSubmitted={handleSubmitted}
          initial={editApp}
          editId={editApp.id}
        />
      )}
      </div>
      </div>
    </div>
  );
}
